import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem, Landmark, Leaf, Award, ArrowRight } from 'lucide-react';
import Header from './components/Header3';
import Footer from './components/Footer3';

const About3 = () => {
    const navigate = useNavigate();

    const values = [
        { icon: Gem, title: 'Artisanship', desc: 'Every stitch tells a story. Our master artisans bring generations of craft knowledge to each creation, ensuring unparalleled quality and attention to detail.' },
        { icon: Landmark, title: 'Heritage', desc: 'Rooted in the rich textile traditions of India, we honour time-honoured techniques while reimagining them for the modern connoisseur.' },
        { icon: Leaf, title: 'Sustainability', desc: 'We are committed to ethical sourcing, sustainable materials, and fair-trade practices that respect both artisan communities and the environment.' },
        { icon: Award, title: 'Excellence', desc: 'From concept to creation, we pursue perfection relentlessly. Each piece undergoes rigorous quality assessment before reaching your hands.' },
    ];

    const milestones = [
        { year: '2010', title: 'The Genesis', desc: 'Founded in a historic Jaipur haveli with a vision to redefine Indian luxury fashion for the contemporary world.' },
        { year: '2013', title: 'Debut Collection', desc: 'Launched our inaugural couture line at India Couture Week, earning critical acclaim and a devoted following.' },
        { year: '2016', title: 'International Debut', desc: 'Showcased at Milan Fashion Week and expanded to serve discerning clientele across 15 countries.' },
        { year: '2019', title: 'Heritage Revival', desc: 'Committed to 100% sustainable sourcing and launched our heritage handloom revival programme.' },
        { year: '2022', title: 'Digital Atelier', desc: 'Launched our immersive digital platform, bringing the bespoke experience to clients worldwide.' },
        { year: '2025', title: 'Global Recognition', desc: 'Honoured with the International Fashion Excellence Award, cementing our position as a global luxury brand.' },
    ];

    const stats = [
        { number: '10,000+', label: 'Happy Clients' },
        { number: '15+', label: 'Years of Craft' },
        { number: '500+', label: 'Bespoke Designs' },
        { number: '50+', label: 'Awards Won' },
    ];

    return (
        <>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700&family=Lato:wght@300;400;700&display=swap');`}</style>
            <div style={{ fontFamily: "'Lato', sans-serif" }} className="min-h-screen bg-[#FFFDF7]">
                <Header />

                <main>
                    {/* Hero Section */}
                    <section className="relative w-full min-h-[60vh] lg:min-h-[75vh] overflow-hidden">
                        <img src="https://images.pexels.com/photos/2916387/pexels-photo-2916387.jpeg?auto=compress&cs=tinysrgb&w=800" alt="About VASTRA" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(114,47,55,0.9) 0%, rgba(114,47,55,0.4) 50%, transparent 100%)' }} />
                        <div className="relative h-full min-h-[60vh] lg:min-h-[75vh] flex items-end">
                            <div className="max-w-[1400px] mx-auto px-4 lg:px-8 pb-16 lg:pb-24 w-full">
                                <div className="w-16 h-px mb-6" style={{ backgroundColor: '#B8860B' }} />
                                <h1 style={{ fontFamily: "'Playfair Display', serif" }} className="text-4xl lg:text-6xl font-bold text-[#FFFFFF] mb-4">Our Story</h1>
                                <p className="text-base lg:text-lg text-[#FFFFFF]/80 max-w-lg">A legacy of royal Indian craftsmanship, reimagined for the modern world.</p>
                            </div>
                        </div>
                    </section>

                    {/* Brand Story */}
                    <section className="py-16 lg:py-24 px-4 lg:px-8">
                        <div className="max-w-[900px] mx-auto text-center">
                            <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{ color: '#B8860B' }}>Est. 2010</p>
                            <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-3xl lg:text-4xl font-bold text-[#722F37] mb-8">Centuries of Craft, One Garment at a Time</h2>
                            <p className="text-base leading-relaxed mb-6" style={{ color: '#6B7280' }}>
                                VASTRA was born from a deep reverence for India's extraordinary textile heritage. In a world of fast fashion, we chose to honour the patient hands of our artisans — the weavers of Varanasi, the embroiderers of Lucknow, the dyers of Rajasthan.
                            </p>
                            <p className="text-base leading-relaxed" style={{ color: '#6B7280' }}>
                                Every garment we create is a dialogue between ancient tradition and contemporary sensibility. We believe that true luxury lies not in excess, but in the irreplaceable value of human craft — garments that carry within their threads the stories, prayers, and artistry of generations.
                            </p>
                        </div>
                    </section>

                    {/* Stats */}
                    <section className="py-12 lg:py-16" style={{ backgroundColor: '#722F37' }}>
                        <div className="max-w-[1000px] mx-auto px-4 grid grid-cols-2 lg:grid-cols-4 gap-8">
                            {stats.map((stat, idx) => (
                                <div key={idx} className="text-center">
                                    <p className="text-3xl lg:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: '#B8860B' }}>{stat.number}</p>
                                    <p className="text-sm mt-2 uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.7)' }}>{stat.label}</p>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Values */}
                    <section className="py-16 lg:py-24 px-4 lg:px-8" style={{ backgroundColor: '#FFF9F0' }}>
                        <div className="max-w-[1200px] mx-auto">
                            <div className="text-center mb-12">
                                <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{ color: '#B8860B' }}>What We Stand For</p>
                                <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-3xl lg:text-4xl font-bold text-[#722F37]">Our Values</h2>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {values.map((val, idx) => (
                                    <div key={idx} className="p-6 text-center" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E8E0D8' }}>
                                        <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: 'rgba(114,47,55,0.05)', border: '1px solid rgba(114,47,55,0.1)' }}>
                                            <val.icon size={24} style={{ color: '#722F37' }} />
                                        </div>
                                        <h3 className="text-base font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}>{val.title}</h3>
                                        <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{val.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Timeline */}
                    <section className="py-16 lg:py-24 px-4 lg:px-8">
                        <div className="max-w-[800px] mx-auto">
                            <div className="text-center mb-12">
                                <p className="text-xs font-bold tracking-[0.3em] uppercase mb-3" style={{ color: '#B8860B' }}>Our Journey</p>
                                <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-3xl lg:text-4xl font-bold text-[#722F37]">Milestones</h2>
                            </div>
                            <div className="space-y-8">
                                {milestones.map((ms, idx) => (
                                    <div key={idx} className="flex gap-6">
                                        <div className="flex flex-col items-center">
                                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#722F37' }} />
                                            {idx < milestones.length - 1 && <div className="w-px flex-1 mt-2" style={{ backgroundColor: '#E8E0D8' }} />}
                                        </div>
                                        <div className="pb-8">
                                            <p className="text-sm font-bold mb-1" style={{ color: '#B8860B' }}>{ms.year}</p>
                                            <h4 className="text-base font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif", color: '#722F37' }}>{ms.title}</h4>
                                            <p className="text-sm leading-relaxed" style={{ color: '#6B7280' }}>{ms.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* CTA */}
                    <section className="py-16 lg:py-20 px-4 text-center" style={{ backgroundColor: '#FFF9F0', borderTop: '1px solid #E8E0D8' }}>
                        <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-2xl lg:text-3xl font-bold text-[#722F37] mb-4">Experience Royal Indian Couture</h2>
                        <p className="text-sm mb-8" style={{ color: '#6B7280' }}>Explore our latest collections and discover the artistry behind every piece.</p>
                        <button onClick={() => navigate('/template3/shop')} className="inline-flex items-center gap-3 px-10 py-4 text-sm font-bold tracking-[0.2em] uppercase transition-all duration-300" style={{ backgroundColor: '#722F37', color: '#FFFFFF' }} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#5A1F2A'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#722F37'; }}>
                            Shop Now <ArrowRight className="w-4 h-4" />
                        </button>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default About3;
